<template>
  <div ref="dashboardContainer"></div>
</template>

<script>
import Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import Russian from "@uppy/locales/lib/ru_RU";
import English from "@uppy/locales/lib/en_US";
import AwsS3 from "@uppy/aws-s3";
import { s3PublicUrl } from "@/utils/s3PublicUrl";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import { RASTER_IMAGE_TYPES } from "@/utils/uploadFileTypes";

export default {
  props: {
    bookingId: {
      type: [Number, String],
      required: true,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ['checkin', 'checkout'].includes(value)
    },
    file: {
      type: File,
      required: false,
      default: null
    },
    // Для загрузки фото конкретного повреждения
    bookingDamageId: {
      type: Number,
      required: false,
      default: null
    },
    // Тип загрузки: 'damage_image' (общая схема) или 'damage_photo' (фото конкретного повреждения)
    uploadType: {
      type: String,
      required: false,
      default: 'damage_image',
      validator: (value) => ['damage_image', 'damage_photo'].includes(value)
    }
  },
  data() {
    return {
      // FT-152: источник истины по id записи — карта компонента, чтобы повтор
      // переиспользовал ту же строку, а не плодил пустые.
      photoIdByFile: {},
      uppy: null
    };
  },
  mounted() {
    console.log('=== UppyDamageImage mounted ===');
    console.log('bookingId:', this.bookingId);
    console.log('mode:', this.mode);
    console.log('bookingDamageId:', this.bookingDamageId);
    console.log('uploadType:', this.uploadType);
    
    this.instantiateUppy();
  },
  beforeDestroy() {
    if (this.uppy) {
      this.uppy.close();
    }
  },
  methods: {
    instantiateUppy() {
      const allowedFileTypes = this.uploadType === 'damage_photo'
        ? RASTER_IMAGE_TYPES
        : ['image/png'];
      
      const note = this.uploadType === 'damage_photo'
        ? (this.$i18n.locale === "ru" ? 'Загрузка фото повреждения...' : 'Uploading damage photo...')
        : (this.$i18n.locale === "ru" ? 'Загрузка изображения повреждений...' : 'Uploading damage image...');

      this.uppy = new Uppy({
        debug: false,
        locale: this.$i18n.locale === "ru" ? Russian : English,
        autoProceed: true,
        allowMultipleUploads: false,
        restrictions: {
          maxFileSize: 10 * 1024 * 1024, // 10 MB
          maxNumberOfFiles: 1,
          allowedFileTypes: allowedFileTypes
        }
      })
      .use(Dashboard, {
        target: this.$refs.dashboardContainer,
        hideUploadButton: true,
        theme: this.$vuetify.theme.dark ? 'dark' : 'light',
        inline: true,
        width: '100%',
        height: this.uploadType === 'damage_photo' ? 350 : 200,
        showProgressDetails: true,
        note: note,
        proudlyDisplayPoweredByUppy: false
      })
      .use(AwsS3, {
        companionUrl: '',
        limit: 1,
        timeout: 60000,
        getUploadParameters: async (file) => {
          try {
            // Определяем эндпоинт в зависимости от типа загрузки
            const endpoint = this.uploadType === 'damage_photo'
              ? `booking_photos/${this.bookingId}/presigned_url`
              : `booking_damage_images/${this.bookingId}/presigned_url`;
            
            const baseUrl = `${this.$store.getters.getApiUrl}/api/v1`; // FT-018
            
            const requestBody = this.uploadType === 'damage_photo'
              ? {
                  filename: file.name,
                  content_type: file.type,
                  checkin: this.mode === 'checkin',
                  booking_damage_id: this.bookingDamageId,
                  // FT-152 REQ-04: повтор переиспользует ту же запись.
                  booking_photo_id: this.photoIdByFile[file.id]
                }
              : {
                  filename: file.name,
                  content_type: file.type,
                  size: file.size,
                  mode: this.mode
                };

            const response = await fetch(`${baseUrl}/${endpoint}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': this.$store.getters.getAuthToken
              },
              body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (!response.ok) {
              console.error('Error getting presigned URL:', data);
              throw new Error(data.error || 'Failed to get presigned URL');
            }

            file.meta = file.meta || {};
            file.meta.key = data.key;
            file.meta.bucket = data.bucket;
            
            // Для damage_photo сохраняем booking_photo_id
            if (this.uploadType === 'damage_photo') {
              file.meta.booking_photo_id = data.booking_photo_id;
              this.photoIdByFile[file.id] = data.booking_photo_id;
            }

            return {
              method: 'POST',
              url: data.presigned_url,
              fields: data.fields,
              fieldName: 'file'
            };
          } catch (error) {
            console.error('Error in getUploadParameters:', error);
            throw error;
          }
        }
      });

      this.uppy.on('upload-success', async (file, response) => {
        if (!file.meta || !file.meta.key) {
          console.error('Missing metadata:', file.meta);
          return;
        }

        // Бакет приходит от сервера вместе с presign. Дефолт больше не прод-бакет:
        // на стенде он давал битое превью (файл в rentprogstaging, ссылка в rentprog).
        const imageUrl = file.meta.bucket
          ? `https://storage.yandexcloud.net/${file.meta.bucket}/${file.meta.key}`
          : s3PublicUrl(file.meta.key);

        // FT-152: исход подтверждения сначала становится состоянием ФАЙЛА и только потом
        // уходит наружу.
        //
        // Порядок здесь не косметика. Наверху (CarDamageMarker) на upload-error висит
        // обработчик, который раньше сносил диалог вместе с самим загрузчиком, — то есть
        // уничтожал кнопку повтора прежде, чем сотрудник успевал её увидеть. И наружу
        // отдаёт РОВНО ОДИН путь: общий this.uppy.on('upload-error') ниже. Пока событие
        // дублировалось прямым $emit, на каждый отказ приходило по два тоста.
        const failFile = (reason) => {
          console.error('Error confirming upload:', reason);
          this.uppy.emit('upload-error', file,
            new Error(this.$t('bookings.photo_confirm_failed')));
        };

        let payload = null;

        try {
          if (this.uploadType === 'damage_photo') {
            // Подтверждение загрузки фото повреждения
            const booking_photo_id = file.meta.booking_photo_id;

            const confirmResponse = await fetch(
              `${this.$store.getters.getApiUrl}/api/v1/booking_photos/${booking_photo_id}/confirm_upload`, // FT-018
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': this.$store.getters.getAuthToken
                },
                body: JSON.stringify({
                  key: file.meta.key,
                  filename: file.name,
                  size: file.size,
                  content_type: file.type
                })
              }
            );

            const confirmData = await confirmResponse.json();

            if (!confirmResponse.ok) {
              failFile(confirmData);
              return;
            }

            payload = {
              booking_photo_id: booking_photo_id,
              image_url: imageUrl,
              photo_data: confirmData
            };
          } else {
            // Подтверждение загрузки общей схемы повреждений
            const confirmResponse = await fetch(
              `${this.$store.getters.getApiUrl}/api/v1/booking_damage_images/${this.bookingId}/confirm_upload`, // FT-018
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': this.$store.getters.getAuthToken
                },
                body: JSON.stringify({
                  mode: this.mode,
                  image_url: imageUrl
                })
              }
            );

            const confirmData = await confirmResponse.json();

            if (!confirmResponse.ok) {
              failFile(confirmData);
              return;
            }

            payload = {
              image_url: imageUrl,
              mode: this.mode
            };
          }
        } catch (error) {
          // Сеть оборвалась или ответ не разобрался. Раньше эта ветка только сообщала
          // наружу и файл не помечала вовсе: загрузка выглядела успешной, строка в базе
          // оставалась пустой — самый тихий из отказов.
          failFile(error);
          return;
        }

        // Наружу — уже за пределами try, чтобы отказ обработчика родителя не выдал
        // доставленную фотографию за упавшую.
        this.$emit('upload-success', payload);
      });

      this.uppy.on('upload-error', (file, error, response) => {
        console.error('Upload error:', {
          file: file.name,
          error: error.message || error,
          response: response || 'No response'
        });
        this.$emit('upload-error', error);
      });

      // Добавляем файл в uppy, если он передан
      if (this.file) {
        this.uppy.addFile({
          name: this.file.name,
          type: this.file.type,
          data: this.file,
          source: 'Local',
          isRemote: false
        });
      }
    }
  }
};
</script>

<style scoped>
</style>

